import { createReducer, on } from '@ngrx/store';
import {
  addMessage,
  addMessageSubFlow,
  addMessages,
  addOneIndexSelectActionBox,
  addSubFlow,
  addedNewItem,
  cleanLastActionAdded,
  cleanMessage,
  inserIntoPosition,
  removeMessage,
  removeMessageSubFlow,
  removeOptionCondition,
  removeSubFlow,
  selectAction,
  setFlow,
  setSelectActionBoxPosition,
  setSelectedConditionals,
  setSubFlowIdActionClicked,
  toggleSelectActionBox,
  updateMessage,
  updateMessageSubFlow,
  updateSubFlow,
  addUpdatedSubflowIdFlowDesign,
  clearUpdatedSubflowIdFlowDesign,
  updateAllActionPositions,
  setNewItemPosition,
  addAllSubFlows,
  setConditionalPath,
  setActionsSnapshot,
  detachAction,
  removeDetachedAction,
} from './step-actions.action';
import { ActionMessages, SubFlowStore } from '../../types/step-actions';
import { FlowI } from '@interfaces/flow.interface';
import { ActionTypeE } from '@enums/action-type.enum';

export interface IStepActions {
  actionSelected:
    | {
        _id: string;
        name: string;
        description: string;
        customerID: string;
        status: string;
        actions: [];
        workFlowId: string;
        createdAt: string;
        updateAt: string;
      }
    | undefined;
  actions: Array<ActionMessages>;
  flow: Partial<FlowI>;
  isSelectActionBoxVisisible: boolean;
  selectActionBoxPosition: number | undefined;
  selectedConditionals: any[];
  selectPathConditionals: { parent: string; id: string }[];
  subFlowIdActionClicked: string;
  parentActionType: string;
  subFlowName: string;
  lastActionAdded: string;
  subFlows: SubFlowStore[];
  updatedSubflowIdsFlowDesign: string[];
  nodesFlowDesignOrganized: boolean;
  addedNewItem: number;
  newItemPosition?: { posX: number; posY: number };
  /** Ações removidas do fluxo mas preservadas (reutilizáveis na lista "Criadas"). */
  detachedActions: ActionMessages[];
}
export const initialState: IStepActions = {
  actionSelected: undefined,
  actions: [],
  flow: {},
  isSelectActionBoxVisisible: true,
  selectActionBoxPosition: 0,
  subFlowIdActionClicked: '',
  parentActionType: '',
  subFlowName: '',
  lastActionAdded: '',
  selectedConditionals: [],
  selectPathConditionals: [],
  subFlows: [],
  updatedSubflowIdsFlowDesign: [],
  nodesFlowDesignOrganized: false,
  addedNewItem: 0,
  detachedActions: [],
};

export const selectActions = (state: IStepActions) => state.actions;

function resortActions(actions: Array<any>): Array<any> {
  for (let i = 0; i < actions.length; i++) {
    const action = actions[i];
    if (action.type === 'conditional' || action.type === 'conditional_template' || action.type === 'optionals') {
      if (Array.isArray(action.body)) {
        actions[i].body = action.body.sort((a: any, b: any) => (a.value > b.value ? 1 : a.value < b.value ? -1 : 0));
        (actions[i].body as Array<any>).forEach((item, index, items) => {
          items[index].value = index + 1;
        });
      } else if (typeof action.body === 'object') {
        actions[i].body.actions = resortActions(actions[i].body.actions);
      }
    }
  }
  return actions;
}

function isResumeItemAction(action: ActionTypeE): boolean {
  return [ActionTypeE.SendMessage, ActionTypeE.RequestInfo].includes(action);
}
function updateSendEmailActionsWithResumeItensActions(
  action: ActionMessages,
  state: IStepActions,
  payload: Partial<ActionMessages>,
  toDelete?: boolean
) {
  if (toDelete) {
    const { actions, subFlows } = state;
    let allActions = [...actions];
    subFlows?.forEach((subFlow) => {
      allActions = allActions.concat(subFlow.actions ?? []);
    });
    const actionId = action.id;
    const actionsToChange = allActions.filter(
      (actionItem) =>
        actionItem.type === ActionTypeE.SendEmail && actionItem.body?.resumeItems?.find((item) => item.id === actionId)
    );

    actionsToChange.forEach((action) => {
      let message: string = action.body?.message;
      if (message) {
        const idMap = action.body?.resumeItems?.find((item) => item.id === actionId);
        const expr = '(\\|([^\\|]+)resposta-resumo-item-' + idMap.shortId + '\\|)';
        message = message.replace(new RegExp(expr, 'g'), ``);
        action.body.message = message;
        action.body.resumeItems = action.body?.resumeItems?.filter((item) => item.id !== actionId);
        action.message = message;
      }
    });
  } else if (isResumeItemAction(action.type as ActionTypeE)) {
    const { actions, subFlows } = state;
    let allActions = [...actions];
    subFlows?.forEach((subFlow) => {
      allActions = allActions.concat(subFlow.actions ?? []);
    });
    const actionId = action.id;
    const actionsToChange = allActions.filter(
      (actionItem) =>
        actionItem.type === ActionTypeE.SendEmail && actionItem.body?.resumeItems?.find((item) => item.id === actionId)
    );
    const oldMessage = (action?.body?.message ?? '').replace(/(<([^>]+)>)/gi, '');
    const newMessage = (payload.body?.message ?? '').replace(/(<([^>]+)>)/gi, '');
    if (oldMessage !== newMessage) {
      actionsToChange.forEach((action) => {
        let message: string = action.body?.message;
        if (message) {
          const idMap = action.body?.resumeItems?.find((item) => item.id === actionId);
          const expr = '(\\|([^\\|]+)resposta-resumo-item-' + idMap.shortId + '\\|)';
          message = message.replace(new RegExp(expr, 'g'), `|${newMessage} resposta-resumo-item-${idMap.shortId}|`);
          action.body.message = message;
          action.message = message;
        }
      });
    }
  }
}
export const stepActionsReducer = createReducer(
  initialState,
  on(setConditionalPath, (state, { payload }) => {
    let selectPathConditionals = state.selectPathConditionals ?? [];
    const idx = selectPathConditionals.findIndex((item) => item.parent === payload.parent); //if action on parent changed , deleted post actions
    if (idx !== -1) {
      selectPathConditionals = selectPathConditionals.filter((_, index) => index < idx);
    }
    selectPathConditionals.push(payload);
    return {
      ...state,
      selectPathConditionals,
    };
  }),
  on(cleanMessage, (state) => ({
    ...state,
    actions: [],
  })),
  on(cleanLastActionAdded, (state) => ({
    ...state,
    lastActionAdded: '',
  })),
  on(updateMessage, (state, { payload }) => ({
    ...state,
    actions: state.actions.map((action) => {
      if (action.id === payload.id) {
        updateSendEmailActionsWithResumeItensActions(action, state, payload);
        return {
          ...action,
          ...payload,
        };
      }
      return action;
    }),
  })),
  on(addSubFlow, (state, { payload }) => {
    if (!state.subFlows.find((s) => s._id === payload._id)) {
      state.subFlows = [...state.subFlows, payload];
    }
    return state;
  }),
  on(addAllSubFlows, (state, { payload }) => {
    // upsert por _id: um subfluxo já presente como shell vazio (actions: []) deve ser SUBSTITUÍDO
    // pela versão populada vinda do backend (getAllSubflows). O filtro append-only antigo descartava
    // a versão populada quando o id já existia → o subfluxo referenciado nunca renderizava as ações.
    const byId = new Map(state.subFlows.map((s) => [s._id, s]));
    for (const incoming of payload) {
      const existing = byId.get(incoming._id);
      // nunca troca algo com ações por um shell vazio; substitui shell vazio (ou menos ações) pela versão maior
      if (!existing || (incoming?.actions?.length ?? 0) > (existing?.actions?.length ?? 0)) {
        byId.set(incoming._id, existing ? { ...existing, ...incoming } : incoming);
      }
    }
    state.subFlows = [...byId.values()];
    return state;
  }),
  on(updateSubFlow, (state, { payload }) => ({
    ...state,
    subFlows: state.subFlows.map((s) => {
      if (s._id === payload._id || (s._id === payload.oldid && payload.oldid)) {
        return {
          ...s,
          ...payload,
        };
      }
      return s;
    }),
  })),
  on(addMessage, (state, { payload }) => {
    const selectActionBoxPosition = state.selectActionBoxPosition;
    const newItemPosition = state.newItemPosition;
    let updatedActions: any = [];
    payload.flowX = newItemPosition?.posX;
    payload.flowY = newItemPosition?.posY;

    if (selectActionBoxPosition) {
      updatedActions = [
        ...state.actions.slice(0, selectActionBoxPosition - 1),
        payload,
        ...state.actions.slice(selectActionBoxPosition - 1),
      ];
    } else {
      updatedActions = [payload, ...state.actions];
    }

    return {
      ...state,
      actions: updatedActions,
      lastActionAdded: payload.id,
    };
  }),

  on(addOneIndexSelectActionBox, (state) => ({
    ...state,
    selectActionBoxPosition:
      state.selectActionBoxPosition !== undefined ? state.selectActionBoxPosition + 1 : undefined,
  })),
  on(updateMessageSubFlow, (state, { payload }) => {
    const subFlowIndex = state.subFlows.findIndex((s) => s._id === state.subFlowIdActionClicked);
    if (subFlowIndex === -1) {
      return { ...state };
    }
    const subFlowsCopy = state.subFlows.map((sf, i) => {
      if (i !== subFlowIndex) return sf;
      return {
        ...sf,
        actions: sf.actions.map((a) => (a.id === payload.id ? { ...a, ...payload } : a)),
      };
    });
    return {
      ...state,
      subFlows: subFlowsCopy,
    };
  }),
  on(addMessageSubFlow, (state, { payload, subFlowId }) => {
    const _id = subFlowId ? subFlowId : state.subFlowIdActionClicked;
    const subFlowIndex = state.subFlows.findIndex((s) => s._id === _id);
    const newItemPosition = state.newItemPosition;
    payload.flowX = newItemPosition?.posX;
    payload.flowY = newItemPosition?.posY;

    if (subFlowIndex !== -1) {
      const subFlowsCopy = [...state.subFlows];

      const subFlow = subFlowsCopy[subFlowIndex];
      const actionsCopy = [...subFlow.actions];

      if (state.selectActionBoxPosition !== undefined) {
        actionsCopy.splice(state.selectActionBoxPosition, 0, payload);
      } else {
        actionsCopy.push(payload);
      }

      subFlowsCopy[subFlowIndex] = {
        ...subFlow,
        actions: actionsCopy,
      };

      return {
        ...state,
        subFlows: subFlowsCopy,
        lastActionAdded: payload.id,
      };
    }

    return state;
  }),
  on(addMessages, (state, { payload }) => ({
    ...state,
    actions: [...state.actions, ...payload],
  })),
  on(toggleSelectActionBox, (state, { isVisible }) => ({
    ...state,
    isSelectActionBoxVisisible: isVisible,
  })),
  on(setSelectActionBoxPosition, (state, { position, subFlowId, parentActionType, subFlowName }) => ({
    ...state,
    selectActionBoxPosition: position,
    subFlowIdActionClicked: subFlowId ? subFlowId : '',
    parentActionType: parentActionType ?? '',
    subFlowName: subFlowName ?? '',
  })),
  on(setSubFlowIdActionClicked, (state, { subFlowId }) => ({
    ...state,
    subFlowIdActionClicked: subFlowId ? subFlowId : '',
  })),
  on(setFlow, (state, { payload }) => ({
    ...state,
    flow: payload,
  })),
  on(setActionsSnapshot, (state, { actions, subFlows, detachedActions }) => ({
    ...state,
    actions: [...actions],
    subFlows: [...subFlows],
    detachedActions: detachedActions ? [...detachedActions] : state.detachedActions,
  })),
  on(detachAction, (state, { payload }) => {
    if (!payload?.id || state.detachedActions.some((a) => a.id === payload.id)) {
      return state;
    }
    return { ...state, detachedActions: [...state.detachedActions, payload] };
  }),
  on(removeDetachedAction, (state, { id }) => ({
    ...state,
    detachedActions: state.detachedActions.filter((a) => a.id !== id),
  })),
  on(selectAction, (state, { payload }) => ({
    ...state,
    actionSelected: payload,
  })),
  on(inserIntoPosition, (state, action) => {
    const newArray = [...state.actions];
    newArray.splice(action.position, 0, action.payload);
    return {
      ...state,
      actions: newArray,
    };
  }),
  on(removeMessage, (state, { payload }) => {
    return {
      ...state,
      actions: state.actions
        .map((action) => {
          if (action.id === payload.id) {
            updateSendEmailActionsWithResumeItensActions(action, state, payload, true);
            return {
              ...action,
              ...payload,
            };
          }
          return action;
        })
        .filter((item) => item.id !== payload.id),
    };
  }),
  on(removeMessageSubFlow, (state, { payload }) => {
    return {
      ...state,
      subFlows: state.subFlows.map((s) => {
        if (s._id === payload.subFlowId) {
          return {
            ...s,
            actions: s.actions.filter((a) => a.id !== payload.action.id),
          };
        }
        return s;
      }),
    };
  }),
  on(removeSubFlow, (state, { payload }) => {
    return {
      ...state,
      subFlows: state.subFlows.filter((subflow) => subflow._id !== payload.id),
    };
  }),
  on(removeOptionCondition, (state, { payload }) => {
    const result = {
      ...state,
      actions: state.actions.map((a) => {
        if (a.id !== payload.actionId) {
          return a;
        } else {
          return {
            ...a,
            body: {
              ...a.body,
              actions: a.body?.actions?.map((ac) => {
                if (ac.name === 'optionals') {
                  return {
                    ...ac,
                    body: ac?.body?.filter((b) => b.parent !== payload.optionId),
                  };
                } else {
                  return {
                    ...ac,
                    body: ac?.body?.filter((b) => b._id !== payload.optionId),
                  };
                }
              }),
            },
          };
        }
      }),
      subFlows: state.subFlows.map((s) => {
        if (s._id === payload?.id) {
          return {
            ...s,
            actions: s.actions.map((a) => {
              if (a.id === payload.actionId) {
                return {
                  ...a,
                  body: {
                    actions: a.body?.actions?.map((ac) => {
                      if (ac.name === 'optionals') {
                        return {
                          ...ac,
                          body: ac?.body?.filter((b) => b.parent !== payload.optionId),
                        };
                      } else {
                        return {
                          ...ac,
                          body: ac?.body?.filter((b) => b._id !== payload.optionId),
                        };
                      }
                    }),
                  },
                };
              }
              return a;
            }),
          };
        }
        return s;
      }),
    };
    result.actions = resortActions(result.actions ?? []);
    const subFlows = result.subFlows ?? [];
    for (let i = 0; i < subFlows.length; i++) {
      subFlows[i].actions = resortActions(subFlows[i].actions ?? []);
    }
    return result;
  }),
  on(addedNewItem, (state) => {
    return {
      ...state,
      addedNewItem: state.addedNewItem + 1,
    };
  }),
  on(setSelectedConditionals, (state, { payload }) => {
    return {
      ...state,
      selectedConditionals: payload,
    };
  }),
  on(addUpdatedSubflowIdFlowDesign, (state, { subflowId }) => {
    if (state.updatedSubflowIdsFlowDesign.includes(subflowId)) {
      return state;
    }
    return { ...state, updatedSubflowIdsFlowDesign: [...state.updatedSubflowIdsFlowDesign, subflowId] };
  }),
  on(clearUpdatedSubflowIdFlowDesign, (state) => {
    return { ...state, updatedSubflowIdsFlowDesign: [] };
  }),
  on(updateAllActionPositions, (state, { nodes }) => {
    const messagesUpdated: { id: string; x: number; y: number }[] = [];
    const updatedSubflows: string[] = [];
    const messagesUpdatedSubflows: { subflowId: string; id: string; x: number; y: number }[] = [];
    for (const node of nodes) {
      const subflowId = node.data.subflowId as string;

      if (subflowId) {
        messagesUpdatedSubflows.push({
          subflowId: subflowId,
          id: node.id,
          x: node.posX,
          y: node.posY,
        });
        if (!updatedSubflows.includes(subflowId) && !state.updatedSubflowIdsFlowDesign.includes(subflowId)) {
          updatedSubflows.push(subflowId);
        }
      } else {
        messagesUpdated.push({
          id: node.id,
          x: node.posX,
          y: node.posY,
        });
      }
    }

    const subflows = state.subFlows;
    for (const messageSubflowUpdated of messagesUpdatedSubflows) {
      const subFlowIndex = subflows.findIndex((s) => s._id === messageSubflowUpdated.subflowId);
      if (subFlowIndex !== -1) {
        subflows[subFlowIndex].actions = state.subFlows[subFlowIndex].actions.map((a) => {
          if (a.id === messageSubflowUpdated.id) {
            return {
              ...a,
              flowX: messageSubflowUpdated!.x,
              flowY: messageSubflowUpdated!.y,
            };
          }
          return a;
        });
      }
    }

    return {
      ...state,
      actions: state.actions.map((a) => {
        const updatedMessage = messagesUpdated.find((m) => m.id == a.id);
        if (updatedMessage) {
          return {
            ...a,
            flowX: updatedMessage!.x,
            flowY: updatedMessage!.y,
          };
        }

        return a;
      }),
      subFlows: subflows,
      updatedSubflowIdsFlowDesign: [...state.updatedSubflowIdsFlowDesign, ...updatedSubflows],
    };
  }),
  on(setNewItemPosition, (state, { payload }) => {
    return { ...state, newItemPosition: payload };
  })
);
